/**
 * Ṣatva on stems whose final -is / -us is an unādi affix (Pāṇini 8.3.57 `iṇkoḥ`).
 *
 * The final *s* of `havis` or `cakṣus` becomes *ṣ* before a vowel ending:
 * *haviṣā*, not *havisā*. But 8.3.59 `ādeśapratyayayoḥ` restricts that to an *s*
 * which is itself an affix or a substitute — and this is where spelling stops
 * being a guide:
 *
 *   - `cakṣus` is √cakṣ + the unādi affix `usi`. The *s* is an affix, so ṣatva
 *     applies: *cakṣuṣā*.
 *   - `su-pis` is su + √piṣ used as a root-noun. The *s* is the root's own final
 *     consonant, not an affix, so ṣatva does **not** apply: *supisā*.
 *
 * The two are indistinguishable by shape, so this cannot be decided by a suffix
 * test. It is decided by the list below, which records the -is/-us stems that
 * are unādi formations. A stem qualifies if it *ends with* one of them, which
 * also covers compounds — *su-cakṣus* behaves like *cakṣus*.
 *
 * The engine applies the rule itself whenever it is given the derivation, which
 * `lexicon.ts` supplies for the simple stems. This exists for the compounds,
 * which cannot be expressed through the current WebAssembly bindings at all: they
 * offer no samāsa constructor, so a compound has to be declined as an opaque
 * stem, and then there is no affix left for 8.3.59 to see.
 */

const VOWELS = new Set([
    "a", "ā", "i", "ī", "u", "ū", "ṛ", "ṝ", "ḷ", "ḹ", "e", "o",
]);

/**
 * Stems in -is / -us formed with an unādi affix, and so subject to ṣatva.
 * Deliberately a list and not a pattern: see the note above.
 */
const UNADI_SIBILANT_STEMS = [
    // -is
    "arcis", "havis", "jyotis", "sarpis", "barhis", "rocis", "śocis",
    "chandis", "vyathis", "ambaris",
    // -us
    "cakṣus", "dhanus", "āyus", "vapus", "yajus", "tapus", "jatus",
    "cetus", "śīrṣus", "parus", "nabhus",
];

/**
 * Whether this stem ends in an unādi -is/-us, directly or as the final member of
 * a compound.
 */
export const hasUnadiSibilant = (stem: string): boolean =>
    UNADI_SIBILANT_STEMS.some(s => stem === s || stem.endsWith(s));

/**
 * Applies 8.3.57 to one derived form, given the stem it came from.
 * Returns the form unchanged when the rule does not apply.
 */
export const applyUnadiSatva = (form: string, stem: string): string => {
    if (!hasUnadiSibilant(stem)) return form;

    // The stem without its final s: "cakṣu" from "cakṣus".
    const base = stem.slice(0, -1);
    if (!form.startsWith(base)) return form;

    const i = base.length;
    if (form[i] !== "s") return form;

    const next = form[i + 1];
    if (next === undefined || !VOWELS.has(next)) return form;

    return `${form.slice(0, i)}ṣ${form.slice(i + 1)}`;
};
